/**
 * 日次サマリーコンポーネント
 */

import { BookOpen, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { getDailySummary } from '@/api/services/journal'
import type { DailySummaryResponse } from '@/api/types/journal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

type Props = {
  dateStr: string
}

export const DailySummary = ({ dateStr }: Props) => {
  const [data, setData] = useState<DailySummaryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSummary = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getDailySummary(dateStr)
      setData(response)
    } catch {
      setError('サマリーの生成に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [dateStr])

  // 日付変更時にリセット
  useEffect(() => {
    setData(null)
    setError(null)
  }, [dateStr])

  if (!data && !isLoading && !error) {
    return (
      <Card className="bg-linear-to-br from-amber-500/5 to-orange-500/5">
        <CardContent className="p-4 text-center">
          <Sparkles className="size-6 mx-auto mb-2 text-amber-500" />
          <p className="text-sm text-muted-foreground mb-3">AIが今日の振り返りをまとめます</p>
          <Button variant="outline" size="sm" onClick={fetchSummary}>
            <BookOpen className="size-4 mr-1" />
            サマリーを生成
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex gap-2 mt-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-14" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" className="mt-2" onClick={fetchSummary}>
            再試行
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!data) return null

  return (
    <Card className="bg-linear-to-br from-amber-500/5 to-orange-500/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="size-4 text-amber-500" />
          今日のまとめ
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm leading-relaxed">{data.summary}</p>

        {data.highlights.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">ハイライト</p>
            <ul className="text-sm space-y-0.5">
              {data.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.mood_keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {data.mood_keywords.map((kw) => (
              <Badge key={kw} variant="secondary" className="text-xs">
                {kw}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
